import React, { useContext, useMemo } from 'react'
import { Product, ReturingCustomersLayoutContext, groupedProductsType } from '../ReturningCustomersLayout';
import NoPreferenceProductCard from './NoPreferenceProductCard';
import { CatalogSettingContext } from '../../ChooseTreatmentRouter';
import { sorted } from '../../../../utils/helpers';
import { translate } from '../../../../utils/translator';
import ProductCard from './ProductCard';
import { isPromotedPill, matchesPreferredType, titleWithDosageForm } from '../dosageForm';

const ProductSelection = () => {
  const defaultLabelData=null;
  const context = useContext(ReturingCustomersLayoutContext);
  const routerContext = useContext(CatalogSettingContext) as any;

  const noPrefMedicineEnabled = routerContext?.isNoPrefereceTreatmentEnabled; //context?.noPreferenceMedicationFlow;
  const initialProduct = context?.initialProduct;

  const groupedProducts = context?.groupedProducts as groupedProductsType;

  const defaultCatalogID = context?.defaultCatalogID;
  let defaultProduct: any;

  if (groupedProducts && defaultCatalogID) {
    defaultProduct = groupedProducts[Number(defaultCatalogID)]
  }
  const preferredType = context?.preferredType;

  const additionalDataFilter = context?.additionalDataFilter;

  const productCatalogIDs = useMemo(() => {
    
    let productCatalogIDs = Object.keys(groupedProducts || {}).map((key) => parseInt(key))
    //Sort by order:
    productCatalogIDs = sorted(productCatalogIDs,(a, b) => (groupedProducts[a][0].treatments_order - groupedProducts[b][0].treatments_order));

    if (!context?.allowPrescriptionOnlyFlow) {

      //Sort by in stock (pre-orderable products keep their predefined position):
      productCatalogIDs = sorted(productCatalogIDs, (a, b) => {
        const aInStock =  Number(groupedProducts[a].some((product:Product) => product.in_stock) || groupedProducts[a][0]?.is_pre_orderable);
        const bInStock  =  Number(groupedProducts[b].some((product:Product) => product.in_stock) || groupedProducts[b][0]?.is_pre_orderable);
        if (aInStock !== bInStock) return bInStock - aInStock;
        return groupedProducts[a][0].treatments_order - groupedProducts[b][0].treatments_order;
      })
    }

    if (preferredType) {
      productCatalogIDs = sorted(productCatalogIDs, (a, b) => {
        const aMatch = Number(matchesPreferredType(groupedProducts[a][0], preferredType));
        const bMatch = Number(matchesPreferredType(groupedProducts[b][0], preferredType));
        return bMatch - aMatch;
      });
    }

    // Sort by additional_data filter (e.g., treatment_type, symptom)
    if (additionalDataFilter) {
      productCatalogIDs = sorted(productCatalogIDs, (a, b) => {
        const aRaw = groupedProducts[a][0].additional_data;
        const bRaw = groupedProducts[b][0].additional_data;
        const aAdditionalData = typeof aRaw === 'string' ? JSON.parse(aRaw || '{}') : (aRaw || {});
        const bAdditionalData = typeof bRaw === 'string' ? JSON.parse(bRaw || '{}') : (bRaw || {});
        const aMatch = Number(aAdditionalData[additionalDataFilter.key] === additionalDataFilter.value);
        const bMatch = Number(bAdditionalData[additionalDataFilter.key] === additionalDataFilter.value);
        return bMatch - aMatch;
      });
    }

    // HL-12695: the promoted pill (FR Wegovy pill) leads the list.
    productCatalogIDs = sorted(productCatalogIDs, (a, b) => Number(isPromotedPill(groupedProducts[b][0])) - Number(isPromotedPill(groupedProducts[a][0])));

    // Same as cannabis CT: every space-separated word must appear in title / product_title.
    const searchTerm = (context?.searchTerm || '').trim();
    if (searchTerm) {
      const words = searchTerm.split(' ').map((w) => w.toLowerCase()).filter(Boolean);
      productCatalogIDs = productCatalogIDs.filter((id) => {
        const p = groupedProducts[id]?.[0];
        if (!p) return false;
        const hay = [p.title, p.product_title].filter(Boolean).join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }

    return productCatalogIDs

  },[groupedProducts, preferredType, additionalDataFilter, context?.searchTerm])
  
  const isFirstHasLabel = useMemo(() => {
    return (defaultProduct && defaultProduct[0]?.labelData)
    ||(groupedProducts && groupedProducts[productCatalogIDs[0]]?.[0]?.labelData);
  }, [defaultProduct, groupedProducts])

  const isDefaultProductInList = productCatalogIDs.includes(Number(defaultCatalogID));
  // HL-12695: arriving on the promoted pill keeps it in the list (first) instead of a separate block above it.
  const isDefaultProductInline = isPromotedPill(defaultProduct?.[0]);

  return (
    <div id='products-selection' className={`flex flex-col gap-5 md:pr-5 ${isFirstHasLabel ? "mt-5" : ""}`}>
      {
        noPrefMedicineEnabled && initialProduct && <div className='mb-5 flex flex-col gap-7'>
          <NoPreferenceProductCard key={'no-pref'} title={"Aucune préférence de traitement"} />
          <p className='text-xs text-[#98a0af] font-bold px-2'>Options de traitment</p>
        </div>
      }
      {
        defaultProduct && isDefaultProductInList && !isDefaultProductInline && <div className='flex flex-col gap-8 justify-between'>
          <ProductCard labelData={defaultLabelData} key={defaultCatalogID} productCatalogID={Number(defaultCatalogID)} title={titleWithDosageForm(defaultProduct[0].title, defaultProduct[0], context?.languageID)} />
          <p className='text-xs text-[#98a0af] font-bold px-2'>{translate(context?.languageID, "default", "ProductSelection/other_treatments")}</p>
          </div>
      }

      {
        productCatalogIDs.filter((productCatalogID) => {
          return isDefaultProductInline || productCatalogID !== Number(defaultCatalogID)
        }).map((productCatalogID) => {
          return <ProductCard labelData={groupedProducts[productCatalogID][0].labelData} key={productCatalogID} productCatalogID={productCatalogID} title={titleWithDosageForm(groupedProducts[productCatalogID][0].title, groupedProducts[productCatalogID][0], context?.languageID)} />
        })
      }
      {
        productCatalogIDs.length === 0 && (context?.searchTerm || '').trim() ? (
          <p className='text-sm text-[#98a0af] px-2'>
            {translate(context?.languageID, 'choose_treatment', 'default/no_results') || 'No matching treatments'}
          </p>
        ) : null
      }
    </div>
  )
}

export default ProductSelection
